import { useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  FileText,
  ShieldCheck,
  FileSignature,
  Search,
  MessageSquare,
  Download,
  CheckCircle,
  RefreshCw,
  Copy,
  Check,
  AlertCircle,
  Shield,
  Plus,
  Trash2,
  Users,
  Clock,
  Calendar
} from 'lucide-react';
import apiClient from '../services/api';
import useStore from '../store/useStore';


const DataExchangeWorkflow = () => {
  const { addNotification } = useStore();
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [workflowData, setWorkflowData] = useState({
    // Provider URLs
    providerManagementUrl: '',
    providerDspUrl: '',

    // Consumer URLs
    consumerManagementUrl: '',
    consumerDspUrl: '',
    
    // API Key for EDC
    apiKey: localStorage.getItem('edc_api_key') || '',
    
    // Policy data
    policyId: '',
    policyPermissions: [{ action: 'USE', target: '*' }],
    
    // Asset data
    assetId: '',
    assetTitle: '',
    assetDescription: '',
    assetContentType: 'application/json',
    dataUrl: '',
    
    // Contract definition
    contractDefinitionId: '',
    
    // Catalog
    catalogData: null,
    availableOffers: [],
    selectedOffer: null,
    
    // Negotiation
    negotiationId: '',
    contractAgreementId: '',
    negotiationState: '',
    
    // Transfer
    transferId: '',
    destination: {
      '@type': 'DataAddress',
      type: 'HttpData',
      baseUrl: 'https://enact-dataspace.iti.gr/api/v1/data-exchange/data-receiver',
      method: 'POST',
      contentType: 'application/json'
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [stepResults, setStepResults] = useState({});
  const [error, setError] = useState(null);

  // Policy restriction state
  const [policyRestrictions, setPolicyRestrictions] = useState([]);
  const [showAddRestriction, setShowAddRestriction] = useState(false);

  const addRestriction = (type) => {
    const newRestriction = {
      id: Date.now(),
      type,
      ...(type === 'participantId' && { participantId: '' }),
      ...(type === 'timeRestriction' && { dateTime: '' }),
      ...(type === 'timespanRestriction' && { fromDate: '', toDate: '' }),
    };
    setPolicyRestrictions([...policyRestrictions, newRestriction]);
    setShowAddRestriction(false);
  };

  const removeRestriction = (id) => {
    setPolicyRestrictions(policyRestrictions.filter(r => r.id !== id));
  };

  const updateRestriction = (id, field, value) => {
    setPolicyRestrictions(policyRestrictions.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const steps = [
    { id: 'urls', title: 'Configuration', description: 'Set Provider & Consumer URLs', icon: FileText },
    { id: 'policy', title: 'Create Policy', description: 'Define access policy', icon: ShieldCheck },
    { id: 'asset', title: 'Create Asset', description: 'Register data asset', icon: FileSignature },
    { id: 'contract', title: 'Contract Definition', description: 'Link policy & asset', icon: FileSignature },
    { id: 'catalog', title: 'Browse Catalog', description: 'View available assets', icon: Search },
    { id: 'negotiate', title: 'Negotiate Contract', description: 'Start negotiation', icon: MessageSquare },
    { id: 'transfer', title: 'Transfer Data', description: 'Initiate data transfer', icon: Download }
  ];

  const updateWorkflowData = (key, value) => {
    setWorkflowData(prev => ({ ...prev, [key]: value }));
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getErrorMessage = (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.response?.data?.details) {
      return error.response.data.details.join(', ');
    }
    if (error.response?.data) {
      return JSON.stringify(error.response.data);
    }
    return error.message || 'Unknown error occurred';
  };

  // Step 0: URL Configuration
  const renderUrlConfiguration = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold mb-4">EDC Connector Configuration</h3>
      
      {/* API Key */}
      <div className="bg-yellow-900/10 border border-yellow-800 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-400 mb-3 flex items-center gap-2">
          <ShieldCheck size={18} />
          EDC API Key
        </h4>
        <div>
          <label className="label">API Key</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1 font-mono"
              value={workflowData.apiKey}
              onChange={(e) => updateWorkflowData('apiKey', e.target.value)}
            />
            <button
              onClick={() => copyToClipboard(workflowData.apiKey, 'apiKey')}
              className="btn btn-secondary px-3"
            >
              {copiedField === 'apiKey' ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Required for EDC Management API authentication</p>
        </div>
      </div>
      
      {/* Provider Section */}
      <div className="bg-blue-900/10 border border-blue-800 rounded-lg p-4">
        <h4 className="font-semibold text-blue-400 mb-4 flex items-center gap-2">
          <FileText size={18} />
          Provider Connector (UOWM)
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="label">Management API URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.providerManagementUrl}
                onChange={(e) => updateWorkflowData('providerManagementUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.providerManagementUrl, 'providerMgmt')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'providerMgmt' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="label">DSP Endpoint URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.providerDspUrl}
                onChange={(e) => updateWorkflowData('providerDspUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.providerDspUrl, 'providerDsp')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'providerDsp' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Consumer Section */}
      <div className="bg-green-900/10 border border-green-800 rounded-lg p-4">
        <h4 className="font-semibold text-green-400 mb-4 flex items-center gap-2">
          <FileText size={18} />
          Consumer Connector (ITI)
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="label">Management API URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.consumerManagementUrl}
                onChange={(e) => updateWorkflowData('consumerManagementUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.consumerManagementUrl, 'consumerMgmt')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'consumerMgmt' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="label">DSP Endpoint URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.consumerDspUrl}
                onChange={(e) => updateWorkflowData('consumerDspUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.consumerDspUrl, 'consumerDsp')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'consumerDsp' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 1: Create Policy
  const handleCreatePolicy = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build policyConstraints from UI restrictions
      const policyConstraints = policyRestrictions.map(restriction => {
        if (restriction.type === 'participantId' && restriction.participantId) {
          return { type: 'participantId', rightOperand: restriction.participantId };
        }
        if (restriction.type === 'timeRestriction' && restriction.dateTime) {
          return { type: 'dateTime', rightOperand: new Date(restriction.dateTime).toISOString() };
        }
        if (restriction.type === 'timespanRestriction' && restriction.fromDate && restriction.toDate) {
          return {
            type: 'timespan',
            fromDate: new Date(restriction.fromDate).toISOString(),
            toDate: new Date(restriction.toDate).toISOString()
          };
        }
        return null;
      }).filter(Boolean);

      const response = await apiClient.post('/data-exchange/provider/policy', {
        policyId: workflowData.policyId,
        permissions: workflowData.policyPermissions,
        policyConstraints,
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        apiKey: workflowData.apiKey
      });

      const createdPolicyId = response.data.policyId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, policy: response.data }));
      updateWorkflowData('policyId', createdPolicyId);
      setPolicyRestrictions([]);

      addNotification({ type: 'success', message: 'Policy created successfully!' });
      setCurrentStep(2);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);

      addNotification({
        type: 'error',
        message: `Failed to create policy: ${errorMsg}`
      });
    } finally {
      setLoading(false);
    }
  };

  const renderPolicyCreation = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Create Access Policy</h3>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-red-400 font-medium">Error Creating Policy</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="label">Policy ID *</label>
        <input
          type="text"
          className="input"
          
          value={workflowData.policyId}
          onChange={(e) => updateWorkflowData('policyId', e.target.value)}
        />
      </div>

      <div>
        <label className="label">Permissions</label>
        <div className="space-y-2">
          {workflowData.policyPermissions.map((perm, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                className="input"
                
                value={perm.action}
                onChange={(e) => {
                  const newPerms = [...workflowData.policyPermissions];
                  newPerms[idx].action = e.target.value;
                  updateWorkflowData('policyPermissions', newPerms);
                }}
              />
              <input
                type="text"
                className="input"
                
                value={perm.target}
                onChange={(e) => {
                  const newPerms = [...workflowData.policyPermissions];
                  newPerms[idx].target = e.target.value;
                  updateWorkflowData('policyPermissions', newPerms);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Publishing Restrictions */}
      <div className="border-t border-gray-700 pt-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Shield size={16} className="text-blue-400" />
            Publishing Restrictions
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAddRestriction(!showAddRestriction)}
              className="text-xs px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg flex items-center gap-1 transition-colors"
            >
              <Plus size={14} />
              Add Restriction
            </button>
            {showAddRestriction && (
              <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 min-w-48">
                <button
                  onClick={() => addRestriction('participantId')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-t-lg"
                >
                  <Users size={14} className="text-purple-400" />
                  Consumer's Participant ID
                </button>
                <button
                  onClick={() => addRestriction('timeRestriction')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2"
                >
                  <Clock size={14} className="text-yellow-400" />
                  Time Restriction
                </button>
                <button
                  onClick={() => addRestriction('timespanRestriction')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-b-lg"
                >
                  <Calendar size={14} className="text-green-400" />
                  Timespan Restriction
                </button>
              </div>
            )}
          </div>
        </div>

        {policyRestrictions.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No restrictions - policy will allow all participants</p>
        ) : (
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {policyRestrictions.map((restriction) => (
              <div key={restriction.id} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium flex items-center gap-2">
                    {restriction.type === 'participantId' && (
                      <><Users size={12} className="text-purple-400" /> Consumer Participant ID</>
                    )}
                    {restriction.type === 'timeRestriction' && (
                      <><Clock size={12} className="text-yellow-400" /> Time Restriction</>
                    )}
                    {restriction.type === 'timespanRestriction' && (
                      <><Calendar size={12} className="text-green-400" /> Timespan Restriction</>
                    )}
                  </span>
                  <button
                    onClick={() => removeRestriction(restriction.id)}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {restriction.type === 'participantId' && (
                  <div>
                    <input
                      type="text"
                      value={restriction.participantId || ''}
                      onChange={(e) => updateRestriction(restriction.id, 'participantId', e.target.value)}
                      
                      className="input w-full text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the consumer's DSP callback address (REFERRING_CONNECTOR). Only this connector will be able to negotiate contracts.
                    </p>
                  </div>
                )}

                {restriction.type === 'timeRestriction' && (
                  <input
                    type="datetime-local"
                    value={restriction.dateTime || ''}
                    onChange={(e) => updateRestriction(restriction.id, 'dateTime', e.target.value)}
                    className="input w-full text-sm"
                  />
                )}

                {restriction.type === 'timespanRestriction' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">From</label>
                      <input
                        type="datetime-local"
                        value={restriction.fromDate || ''}
                        onChange={(e) => updateRestriction(restriction.id, 'fromDate', e.target.value)}
                        className="input w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">To</label>
                      <input
                        type="datetime-local"
                        value={restriction.toDate || ''}
                        onChange={(e) => updateRestriction(restriction.id, 'toDate', e.target.value)}
                        className="input w-full text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleCreatePolicy}
        disabled={loading || !workflowData.policyId}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
        Create Policy
      </button>

      {stepResults.policy && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
          <p className="text-green-400 font-medium">✓ Policy Created Successfully</p>
          <p className="text-sm text-gray-400 mt-1">Policy ID: <span className="font-mono">{workflowData.policyId}</span></p>
        </div>
      )}
    </div>
  );

  // Step 2: Create Asset
  const handleCreateAsset = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/data-exchange/provider/asset', {
        assetId: workflowData.assetId,
        title: workflowData.assetTitle,
        description: workflowData.assetDescription,
        contentType: workflowData.assetContentType,
        dataUrl: workflowData.dataUrl,
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        apiKey: workflowData.apiKey
      });
      
      const createdAssetId = response.data.assetId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, asset: response.data }));
      updateWorkflowData('assetId', createdAssetId);
      
      addNotification({ type: 'success', message: 'Asset created successfully!' });
      setCurrentStep(3);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to create asset: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderAssetCreation = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Register Data Asset</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-red-400 font-medium">Error Creating Asset</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Asset ID *</label>
          <input
            type="text"
            className="input"
            
            value={workflowData.assetId}
            onChange={(e) => updateWorkflowData('assetId', e.target.value)}
          />
        </div>
        
        <div>
          <label className="label">Content Type</label>
          <select
            className="input"
            value={workflowData.assetContentType}
            onChange={(e) => updateWorkflowData('assetContentType', e.target.value)}
          >
            <option value="application/json">JSON</option>
            <option value="text/csv">CSV</option>
            <option value="application/xml">XML</option>
            <option value="text/plain">Text</option>
          </select>
        </div>
      </div>
      
      <div>
        <label className="label">Title *</label>
        <input
          type="text"
          className="input"
          
          value={workflowData.assetTitle}
          onChange={(e) => updateWorkflowData('assetTitle', e.target.value)}
        />
      </div>
      
      <div>
        <label className="label">Description</label>
        <textarea
          className="input"
          rows={3}
          
          value={workflowData.assetDescription}
          onChange={(e) => updateWorkflowData('assetDescription', e.target.value)}
        />
      </div>
      
      <div>
        <label className="label">Data URL *</label>
        <input
          type="text"
          className="input"
          
          value={workflowData.dataUrl}
          onChange={(e) => updateWorkflowData('dataUrl', e.target.value)}
        />
        <p className="text-xs text-gray-400 mt-1">The actual HTTP endpoint where data can be accessed</p>
      </div>
      
      <button
        onClick={handleCreateAsset}
        disabled={loading || !workflowData.assetId || !workflowData.assetTitle || !workflowData.dataUrl}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <FileSignature size={16} />}
        Create Asset
      </button>
      
      {stepResults.asset && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
          <p className="text-green-400 font-medium">✓ Asset Created Successfully</p>
          <p className="text-sm text-gray-400 mt-1">Asset ID: <span className="font-mono">{workflowData.assetId}</span></p>
        </div>
      )}
    </div>
  );

  // Step 3: Create Contract Definition
  const handleCreateContractDefinition = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/data-exchange/provider/contract-definition', {
        contractDefinitionId: workflowData.contractDefinitionId,
        policyId: workflowData.policyId,
        assetId: workflowData.assetId,
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        apiKey: workflowData.apiKey
      });
      
      const createdContractDefId = response.data.contractDefinitionId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, contractDef: response.data }));
      updateWorkflowData('contractDefinitionId', createdContractDefId);
      
      addNotification({ type: 'success', message: 'Contract definition created successfully!' });
      setCurrentStep(4);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to create contract definition: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderContractDefinition = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Create Contract Definition</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div>
        <label className="label">Contract Definition ID *</label>
        <input
          type="text"
          className="input"
          
          value={workflowData.contractDefinitionId}
          onChange={(e) => updateWorkflowData('contractDefinitionId', e.target.value)}
        />
      </div>
      
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <h4 className="font-medium mb-3">Linked Resources</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Policy ID:</span>
            <span className="text-blue-400 font-mono text-xs">{workflowData.policyId || 'Not set'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Asset ID:</span>
            <span className="text-blue-400 font-mono text-xs">{workflowData.assetId || 'Not set'}</span>
          </div>
        </div>
      </div>
      
      <button
        onClick={handleCreateContractDefinition}
        disabled={loading || !workflowData.contractDefinitionId || !workflowData.policyId || !workflowData.assetId}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <FileSignature size={16} />}
        Create Contract Definition
      </button>
      
      {stepResults.contractDef && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
          <p className="text-green-400 font-medium">✓ Contract Definition Created Successfully</p>
          <p className="text-sm text-gray-400 mt-1">
            Contract ID: <span className="font-mono">{workflowData.contractDefinitionId}</span>
          </p>
        </div>
      )}
    </div>
  );

  // Step 4: Browse Catalog
  const handleBrowseCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/data-exchange/consumer/catalog', {
        params: {
          providerUrl: workflowData.providerManagementUrl,
          consumerUrl: workflowData.consumerManagementUrl,
          providerDspUrl: workflowData.providerDspUrl,
          consumerDspUrl: workflowData.consumerDspUrl,
          apiKey: workflowData.apiKey
        }
      });
      
      setStepResults(prev => ({ ...prev, catalog: response.data }));
      updateWorkflowData('catalogData', response.data.catalog);
      updateWorkflowData('availableOffers', response.data.availableOffers || []);
      
      addNotification({ 
        type: 'success', 
        message: `Found ${response.data.availableOffers?.length || 0} available offers!` 
      });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to browse catalog: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderCatalogBrowser = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Browse Provider Catalog</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <button
        onClick={handleBrowseCatalog}
        disabled={loading}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
        Fetch Catalog
      </button>
      
      {workflowData.availableOffers.length > 0 && (
        <div className="space-y-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">Available Assets ({workflowData.availableOffers.length})</h4>
              <span className="text-xs text-gray-400">
                Provider: {stepResults.catalog?.summary?.providerParticipant}
              </span>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {workflowData.availableOffers.map((offer, idx) => (
                <div 
                  key={idx}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    workflowData.selectedOffer?.assetId === offer.assetId
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-gray-700 hover:border-blue-500/50'
                  }`}
                  onClick={() => {
                    updateWorkflowData('selectedOffer', offer);
                    addNotification({ type: 'info', message: `Selected: ${offer.title}` });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-200">{offer.title}</p>
                      <p className="text-sm text-gray-400 mt-1">{offer.description}</p>
                      <p className="text-xs text-gray-500 font-mono mt-2">
                        Asset: {offer.assetId}
                      </p>
                    </div>
                    {workflowData.selectedOffer?.assetId === offer.assetId && (
                      <CheckCircle2 className="text-blue-400 flex-shrink-0 ml-4" size={24} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button
            onClick={() => setCurrentStep(5)}
            disabled={!workflowData.selectedOffer}
            className="btn btn-primary w-full"
          >
            Continue with Selected Asset
            <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );

  // Step 5: Negotiate Contract
  const handleNegotiateContract = async () => {
    setLoading(true);
    setError(null);
    try {
      const offer = workflowData.selectedOffer;
      
      const response = await apiClient.post('/data-exchange/consumer/negotiate', {
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        assetId: offer.assetId,
        offerId: offer.offerId,
        policy: offer.policy,
        providerId: stepResults.catalog?.summary?.providerParticipant || workflowData.providerParticipantId || 'connector-provider',
        apiKey: workflowData.apiKey
      });
      
      const negotiationId = response.data.negotiationId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, negotiation: response.data }));
      updateWorkflowData('negotiationId', negotiationId);
      
      addNotification({ type: 'success', message: 'Contract negotiation started!' });
      setTimeout(() => checkNegotiationStatus(negotiationId), 3000);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to negotiate: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const checkNegotiationStatus = async (negotiationId) => {
    try {
      const response = await apiClient.get(`/data-exchange/consumer/negotiation/${negotiationId}`, {
        params: {
          providerUrl: workflowData.providerManagementUrl,
          consumerUrl: workflowData.consumerManagementUrl,
          providerDspUrl: workflowData.providerDspUrl,
          consumerDspUrl: workflowData.consumerDspUrl,
          apiKey: workflowData.apiKey
        }
      });
      
      setStepResults(prev => ({ ...prev, negotiationStatus: response.data }));
      updateWorkflowData('negotiationState', response.data.state || response.data.status);
      
      if (response.data.contractAgreementId) {
        updateWorkflowData('contractAgreementId', response.data.contractAgreementId);
        addNotification({ type: 'success', message: 'Contract agreement reached!' });
      }
    } catch (error) {
      console.error('Failed to check negotiation status:', error);
    }
  };

  const renderContractNegotiation = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Negotiate Contract</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {workflowData.selectedOffer && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <h4 className="font-medium mb-2">Selected Asset</h4>
          <p className="text-sm text-gray-300">{workflowData.selectedOffer.title}</p>
          <p className="text-xs text-gray-500 font-mono mt-1">{workflowData.selectedOffer.assetId}</p>
        </div>
      )}
      
      <button
        onClick={handleNegotiateContract}
        disabled={loading || !workflowData.selectedOffer}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <MessageSquare size={16} />}
        Start Negotiation
      </button>
      
      
      {workflowData.negotiationId && (
        <div className="space-y-3">
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
            {!workflowData.contractAgreementId ? (
              <p className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                <Loader2 className="animate-spin text-blue-400" size={20} />
                Negotiation In Progress
              </p>
            ) : (
              <p className="text-green-400 font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                Negotiation Complete
              </p>
            )}
            <p className="text-sm text-gray-400">
              Negotiation ID: <span className="font-mono text-xs">{workflowData.negotiationId}</span>
            </p>
            {workflowData.contractAgreementId && (
              <p className="text-green-400 mt-2">
                ✓ Agreement ID: <span className="font-mono text-xs break-all">{workflowData.contractAgreementId}</span>
              </p>
            )}
          </div>
          
          <button
            onClick={() => checkNegotiationStatus(workflowData.negotiationId)}
            className="btn btn-secondary w-full"
            disabled={!!workflowData.contractAgreementId}
          >
            <RefreshCw size={16} />
            Check Status
          </button>
          
          {workflowData.contractAgreementId && (
            <button
              onClick={() => setCurrentStep(6)}
              className="btn btn-primary w-full"
            >
              Continue to Transfer
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}
      
    </div>
  );

  // Step 6: Transfer Data
  const handleTransferData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/data-exchange/consumer/transfer', {
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        contractId: workflowData.contractAgreementId,
        assetId: workflowData.selectedOffer?.assetId,
        destination: workflowData.destination,
        apiKey: workflowData.apiKey
      });
      
      const transferId = response.data.transferId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, transfer: response.data }));
      updateWorkflowData('transferId', transferId);
      
      addNotification({ type: 'success', message: 'Data transfer initiated successfully!' });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to transfer: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderDataTransfer = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Initiate Data Transfer</h3>
      
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <h4 className="font-medium mb-3">Transfer Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Contract Agreement:</span>
            <span className="text-blue-400 text-xs font-mono break-all ml-2">
              {workflowData.contractAgreementId || 'Pending'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Asset ID:</span>
            <span className="text-blue-400 text-xs font-mono">
              {workflowData.selectedOffer?.assetId}
            </span>
          </div>
        </div>
      </div>
      
      <div>
        <label className="label">Destination URL</label>
        <input
          type="text"
          className="input"
          value={workflowData.destination.baseUrl}
          onChange={(e) => updateWorkflowData('destination', {
            ...workflowData.destination,
            baseUrl: e.target.value
          })}
        />
      </div>
      
      <button
        onClick={handleTransferData}
        disabled={loading || !workflowData.contractAgreementId}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
        Start Transfer
      </button>
      
      {workflowData.transferId && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
          <p className="text-green-400 font-medium mb-2">✓ Transfer Initiated Successfully</p>
          <p className="text-sm text-gray-400">
            Transfer ID: <span className="font-mono text-xs">{workflowData.transferId}</span>
          </p>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderUrlConfiguration();
      case 1: return renderPolicyCreation();
      case 2: return renderAssetCreation();
      case 3: return renderContractDefinition();
      case 4: return renderCatalogBrowser();
      case 5: return renderContractNegotiation();
      case 6: return renderDataTransfer();
      default: return renderUrlConfiguration();
    }
  };

  return (
    <div className="p-6 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">EDC Data Exchange Workflow</h1>
        <p className="text-gray-400">Complete end-to-end data exchange</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center justify-between mb-4 min-w-max">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => setCurrentStep(idx)}
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all
                      ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50' : ''}
                      ${isCompleted ? 'bg-green-600 text-white' : ''}
                      ${!isActive && !isCompleted ? 'bg-gray-800 text-gray-400' : ''}
                    `}
                  >
                    {isCompleted ? <CheckCircle2 size={20} /> : <Icon size={20} />}
                  </button>
                  <div className="text-center">
                    <p className={`text-xs font-medium ${isActive ? 'text-blue-400' : 'text-gray-400'}`}>
                      {step.title}
                    </p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 ${isCompleted ? 'bg-green-600' : 'bg-gray-800'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="card max-w-4xl mx-auto">
        {renderStepContent()}
        
        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-800">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          
          {currentStep < steps.length - 1 && currentStep !== 4 && currentStep !== 5 && (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="btn btn-primary"
            >
              Next
              <ArrowRight size={16} />
            </button>
          )}
          
          {currentStep === steps.length - 1 && workflowData.transferId && (
            <button
              onClick={() => {
                setCurrentStep(0);
                setStepResults({});
                addNotification({ type: 'success', message: 'Workflow completed!' });
              }}
              className="btn btn-primary"
            >
              Complete & Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExchangeWorkflow;
